import { Injectable } from "@angular/core";
import { HttpClient, HttpContext, HttpContextToken, HttpHeaders } from "@angular/common/http";
import { Router } from "@angular/router";
import { Observable, from, throwError } from "rxjs";
import { finalize, map, shareReplay } from "rxjs/operators";
import { LocalstorageService } from "./localstorage.service";
import { environment } from "../../environments/environment";

/** Tags the refresh call itself so the interceptor never tries to refresh-and-retry it. */
export const IS_AUTH_REFRESH_REQUEST = new HttpContextToken<boolean>(() => false);

interface RefreshTokenResponseBody {
  tokens: { access_token: string; refresh_token: string };
}

interface DecodedJwtClaims {
  exp?: number;
  iat?: number;
}

/** Pages for signed-out visitors, where an ended session is cleared without leaving the page. */
const PUBLIC_PAGES = ['/invite', '/login', '/verification', '/reset', '/docs', '/policy'];

@Injectable({ providedIn: 'root' })
export class AuthService {
  private accessTokenKey = 'access_token';
  private refreshTokenKey = 'refresh_token';

  /** In-flight refresh, shared by every concurrent caller in this tab. */
  private refresh$: Observable<void> | null = null;

  constructor(
    private http: HttpClient,
    private _LocalstorageService: LocalstorageService,
    private router: Router,
  ) { }

  saveTokens(accessToken: string, refreshToken: string) {
    this._LocalstorageService.set(this.accessTokenKey, accessToken);
    this._LocalstorageService.set(this.refreshTokenKey, refreshToken);
  }

  getAccessToken(): string | null {
    return this._LocalstorageService.get(this.accessTokenKey);
  }

  getRefreshToken(): string | null {
    return this._LocalstorageService.get(this.refreshTokenKey);
  }

  logout() {
    this._LocalstorageService.clear();
    // On a page meant for signed-out visitors (a team invitation link, login…) the visitor stays put:
    // an old session ending there must not throw them off the link they just opened
    if (!this.isOnPublicPage()) {
      this.router.navigate(['/login']);
    }
  }

  /**
   * True on pages a signed-out visitor uses. Read from the address bar, not the router: while the app
   * starts, router.url is still "/" even when the visitor opened /invite/<token>.
   */
  isOnPublicPage(): boolean {
    if (typeof window === 'undefined') return false;
    const path = window.location.pathname;
    return PUBLIC_PAGES.some(page => path === page || path.startsWith(page + '/'));
  }

  get token(): string | null {
    return this.getAccessToken();
  }

  isAuthenticated(): boolean {
    const token = this.token;
    if (!token) return false;
    return !this.isTokenExpired(token);
  }

  /** Full lifespan (exp - iat) of the current access token, in ms. Null if undecodable. */
  getAccessTokenLifespanMs(): number | null {
    const claims = this.decodeToken(this.token);
    if (!claims?.exp || !claims?.iat) return null;
    return (claims.exp - claims.iat) * 1000;
  }

  /** Time remaining before the current access token expires, in ms. Null if undecodable. */
  getAccessTokenExpiresInMs(): number | null {
    const claims = this.decodeToken(this.token);
    if (!claims?.exp) return null;
    return claims.exp * 1000 - Date.now();
  }

  /**
   * Single entry point for token refresh.
   * - Same tab: concurrent callers share one in-flight Observable (shareReplay).
   * - Cross tab: guarded by the Web Locks API so only one tab hits the network;
   *   the losing tab re-reads storage after acquiring the lock and reuses the
   *   already-rotated tokens instead of racing with a stale refresh token.
   */
  refreshTokens(): Observable<void> {
    if (this.refresh$) {
      return this.refresh$;
    }

    const refreshTokenAtCallTime = this.getRefreshToken();

    const runRefresh = async (): Promise<void> => {
      const currentRefreshToken = this.getRefreshToken();
      if (currentRefreshToken && currentRefreshToken !== refreshTokenAtCallTime) {
        // Another tab already rotated the tokens while we were waiting for the lock.
        return;
      }
      const body = await this.performRefresh();
      this.saveTokens(body.tokens.access_token, body.tokens.refresh_token);
    };

    const hasWebLocks = typeof navigator !== 'undefined' && !!navigator.locks;

    const refresh$: Observable<void> = hasWebLocks
      ? from(navigator.locks.request('scripay-token-refresh', () => runRefresh()))
      : from(runRefresh());

    this.refresh$ = refresh$.pipe(
      finalize(() => { this.refresh$ = null; }),
      shareReplay(1),
    );

    return this.refresh$;
  }

  private performRefresh(): Promise<RefreshTokenResponseBody> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return Promise.reject(new Error('No refresh token available'));
    }

    const headers = new HttpHeaders({
      'skip-auth': 'true',
      'skip-activity-tracking': 'true',
    });
    const context = new HttpContext().set(IS_AUTH_REFRESH_REQUEST, true);

    const request$ = this.http.post<RefreshTokenResponseBody>(
      `${environment.apiUrl}authentication/access-token`,
      { refresh_token: refreshToken },
      { headers, context },
    );

    return new Promise((resolve, reject) => {
      request$.subscribe({ next: resolve, error: reject });
    });
  }

  private decodeToken(token: string | null): DecodedJwtClaims | null {
    if (!token) return null;
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  }

  private isTokenExpired(token: string): boolean {
    const claims = this.decodeToken(token);
    if (!claims?.exp) return true;
    return claims.exp * 1000 < Date.now();
  }
}
